import { useState, ReactNode, useCallback } from 'react'
import { Card as MuiCard, ClickAwayListener, styled } from '@mui/material'
import clsx from 'clsx'

export interface CardBaseProps {
  onClick?: React.MouseEventHandler<HTMLDivElement>
  children: ReactNode
  'aria-label'?: string
  className?: string
  hasOverEffect?: boolean
}

const Card = styled(MuiCard, {
  shouldForwardProp: (prop: string) => !['hasAction', 'hasOverEffect'].includes(prop)
})<{
  hasAction?: boolean
  hasOverEffect?: boolean
}>(({ hasAction, hasOverEffect, theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(1),
  '& img.MuiCardMedia-root, & .react-player__preview': {
    transformOrigin: '50% 50%',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard
    })
  },

  ...(hasAction && {
    cursor: 'pointer'
  }),
  ...(hasOverEffect && {
    '& .border': {
      borderColor: theme.palette.text.hint
    },

    '& img.MuiCardMedia-root, & .MuiCardMedia-root .react-player__preview': {
      transform: 'scale(1.2)'
    }
  })
}))

const Div = (props) => <div {...props} className={clsx(props?.className, 'border')}></div>
const Border = styled(Div)(({ theme }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 1,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create('border-color', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard
  })
}))

function CardBase({ onClick, children, className, hasOverEffect = true, 'aria-label': label }: CardBaseProps) {
  const [isOver, setIsOver] = useState<boolean>(false)

  const onMouseEnter = useCallback(() => {
    setIsOver(true)
  }, [setIsOver])

  const onMouseLeave = useCallback(() => {
    setIsOver(false)
  }, [setIsOver])

  return (
    <ClickAwayListener onClickAway={onMouseLeave} mouseEvent="onMouseUp">
      <Card
        aria-label={label}
        elevation={0}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        hasAction={!!onClick}
        hasOverEffect={isOver && hasOverEffect}
        className={className}
        sx={{ position: 'relative' }}
      >
        <Border />
        {children}
      </Card>
    </ClickAwayListener>
  )
}
export default CardBase
